import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  canEditUserProfile,
  canEditUserAdminFields,
  canEditUserLoginActive,
  isFormerMember,
} from '../core/utils/permissionUtils';
import axios from 'axios';
import { Icon } from '@iconify/react/dist/iconify.js';
import { format } from 'date-fns';

import { useAuth } from '../core/AuthContext';
import { refreshUserPermissions } from '../core/store/actions/auth';
import { URL } from '../core/config';
import "../styles/Form.css";
import "../styles/general.css";
import "../styles/UserProfile.css";
import CustomAlert from './CustomAlert';
import CustomAlertWithButtons from './CustomAlertWithButtons';
import LoadingSpinner from './LoadingSpinner';
import DepartmentCheckboxGroup from './departments/DepartmentCheckboxGroup';
import {
  formatPositionOptionLabel,
  getHeadOfPositions,
  extractDepartmentIds,
} from '../core/constants/departments';
import { useDepartments } from '../core/DepartmentsContext';

const usersURL = URL + "/users";
const semestersURL = URL + "/semesters";

const ADDITIONAL_POSITIONS = [
  "Treasury Officer",
  "Activities Officer",
];

const EditProfile = () => {
  const { userId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const authContext = useAuth();
  const contextUser = authContext.user;
  const { departments: catalog } = useDepartments();

  const [user, setUser] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [pictureError, setPictureError] = useState(null);
  const isMountedRef = useRef(true);

  // For CustomAlert
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: '',
    type: '',
    message: '',
    btn1Action: () => { },
    btn2Action: () => { },
    btn1Text: '',
    btn2Text: ''
  });

  // Function to add icon to error feedback
  const withIcon = (message) => (
    <span style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
      <Icon icon="f7:exclamationmark-circle-fill" style={{ marginRight: '5px', marginLeft: '24px' }} /> {message}
    </span>
  );

  // Fetch user data and semesters on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user data
        const userResponse = await axios.get(`${usersURL}/${userId}`);
        const userData = userResponse.data;

        // Format dates for form inputs
        if (userData.birthDate) {
          userData.birthDate = format(new Date(userData.birthDate), 'yyyy-MM-dd');
        }
        if (userData.dateJoined) {
          userData.dateJoined = format(new Date(userData.dateJoined), 'yyyy-MM-dd');
        }
        if (userData.suspension?.start) {
          userData.suspension.start = format(new Date(userData.suspension.start), 'yyyy-MM-dd');
        }
        if (userData.suspension?.end) {
          userData.suspension.end = format(new Date(userData.suspension.end), 'yyyy-MM-dd');
        }

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setUser(userData);
        }

        // Fetch all semesters
        const semestersResponse = await axios.get(`${semestersURL}/list`);
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setSemesters(semestersResponse.data);
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError('Error fetching data: ' + (err.response?.data?.error || err.message));
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, [userId]);

  // Validation schema
  const validationSchema = Yup.object({
    fullName: Yup.string().required(withIcon('Full name is required')),
    firstLastName: Yup.string().required(withIcon('First and last name is required.')),
    nickname: Yup.string(),
    birthDate: Yup.date().required(withIcon('Birth date is required')),
    nationality: Yup.string().required(withIcon('Nationality is required')),
    naturality: Yup.string().required(withIcon('Naturality is required')),
    professionalStatus: Yup.string().required(withIcon('Professional status is required')),
    personalEmail: Yup.string().email(withIcon('Invalid email format')).required(withIcon('Personal email is required')),
    institutionalEmail: Yup.string().email(withIcon('Invalid email format')),
    esnAccountUsername: Yup.string().required(withIcon('ESN account username is required')),
    phone: Yup.string().required(withIcon('Phone number is required')),
    address: Yup.string().required(withIcon('Address is required')),
    emergencyName: Yup.string().required(withIcon('Emergency contact name is required')),
    emergencyRelationship: Yup.string().required(withIcon('Emergency contact relationship is required')),
    emergencyPhone: Yup.string().required(withIcon('Emergency contact phone is required')),
    mobilityPlace: Yup.string(),
    mobilityProgram: Yup.string(),
    dateJoined: Yup.date().required(withIcon('Date joined is required')),
    membershipType: Yup.string().required(withIcon('Membership type is required')),
    departments: Yup.array().required(withIcon('Select at least one department'))
      .min(1, withIcon('Select at least one department'))
      .max(2, withIcon('You can only select at most two departments')),
    position: Yup.string().required(withIcon('Position is required')),
    semesters: Yup.array().min(1, withIcon('Select at least one semester')),
    // Suspension validation
    'suspension.isSuspended': Yup.boolean(),
    // 'suspension.start': Yup.date().when('suspension.isSuspended', {
    //   is: true,
    //   then: Yup.date().required(withIcon('Suspension start date is required')),
    //   otherwise: Yup.date().nullable()
    // }),
    // 'suspension.end': Yup.date().when('suspension.isSuspended', {
    //   is: true,
    //   then: Yup.date()
    //     .required(withIcon('Suspension end date is required'))
    //     .min(Yup.ref('suspension.start'), withIcon('End date must be after start date')),
    //   otherwise: Yup.date().nullable()
    // }),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const updateData = { ...values };

      // Handle suspension data
      if (!updateData.suspension?.isSuspended) {
        updateData.suspension = {
          isSuspended: false,
          start: null,
          end: null
        };
      } else {
        updateData.suspension = {
          isSuspended: true,
          start: updateData.suspension.start || null,
          end: updateData.suspension.end || null,
        };
      }

      if (updateData.loginEmailType === 'personal' && user.personalEmail !== user.email) {
        updateData.email = values.personalEmail
      } else if (updateData.loginEmailType === 'institutional' && user.institutionalEmail !== user.email) {
        updateData.email = values.institutionalEmail
      }

      delete updateData.loginEmailType;

      // Remove fields that shouldn't be updated by regular users
      delete updateData._id;
      delete updateData.__v;
      delete updateData.password;
      delete updateData.achievements;
      delete updateData.reset_token;
      delete updateData.reset_token_expires;

      // Strip admin-only fields when the editor lacks the matching permission
      if (!canEditUserLoginActive(contextUser)) {
        delete updateData.loginActive;
      }
      if (!canEditUserAdminFields(contextUser)) {
        delete updateData.suspension;
        delete updateData.semesters;
      }

      await axios.patch(`${usersURL}/${userId}`, updateData);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setUpdateSuccess(true);
        setAlertInfo({
          show: true,
          type: 'success',
          message: 'This profile was successfully updated!'
        });
      }

      // Redirect back to profile after successful update
      setTimeout(() => {
        history.push(`/users/${userId}`);
      }, 1500);

    } catch (err) {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message;
        // setError('Error updating profile: ' + errorMessage);
        setAlertInfo({
          show: true,
          type: 'error',
          message: errorMessage
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingPicture(true);
    setPictureError(null);

    const formData = new FormData();
    formData.append('profilePicture', file);

    try {
      const response = await axios.put(`${usersURL}/${userId}/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (isMountedRef.current) {
        setUser((prev) => ({ ...prev, profilePictureUrl: response.data.profilePictureUrl }));
      }

      // Keep the global profile (NavHeader, etc.) in sync when editing your own picture
      if (userId === contextUser._id) {
        dispatch(refreshUserPermissions());
      }
    } catch (err) {
      if (isMountedRef.current) {
        setPictureError(err.response?.data?.error || err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setUploadingPicture(false);
      }
      event.target.value = '';
    }
  };

  const performDeleteProfilePicture = async () => {
    setAlertActionInfo((prev) => ({ ...prev, show: false }));
    setUploadingPicture(true);
    setPictureError(null);

    try {
      await axios.delete(`${usersURL}/${userId}/profile-picture`);

      if (isMountedRef.current) {
        setUser((prev) => ({ ...prev, profilePictureUrl: null }));
      }

      // Keep the global profile (NavHeader, etc.) in sync when editing your own picture
      if (userId === contextUser._id) {
        dispatch(refreshUserPermissions());
      }
    } catch (err) {
      if (isMountedRef.current) {
        setPictureError(err.response?.data?.error || err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setUploadingPicture(false);
      }
    }
  };

  const handleDeleteProfilePicture = () => {
    setAlertActionInfo({
      show: true,
      title: 'Remove profile picture?',
      type: 'error',
      message: 'Are you sure you want to remove this profile picture? This action cannot be undone.',
      btn1Action: () => {
        setAlertActionInfo((prev) => ({ ...prev, show: false }));
      },
      btn2Action: performDeleteProfilePicture,
      btn1Text: 'Cancel',
      btn2Text: 'Yes, Remove',
    });
  };

  if (loading) return <div className="page-container"><LoadingSpinner message="Loading profile" /></div>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!user) return <p>User not found</p>;

  // Check if user can edit (must be themselves or have MANAGE_USERS)
  if (!canEditUserProfile(contextUser, userId)) {
    return <p>You don't have permission to edit this profile</p>;
  }

  // Check if current user can edit administrative fields
  const canEditAdminFields = canEditUserAdminFields(contextUser);

  return (
    <div className='page-container'>
      {/* Header */}
      <header style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Link to={`/users/${userId}`} className="button button-transparent-black back-home">
          <i className="uil uil-angle-left-b header-icon small"></i>
          <i className="uil uil-user header-icon small"></i>
        </Link>
        <div className="header">
          <h2 className="page-title">Edit Profile</h2>
        </div>
      </header>

      {updateSuccess && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '10px',
          borderRadius: '5px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          Profile updated successfully! Redirecting...
        </div>
      )}

      <Formik
        initialValues={{
          fullName: user.fullName || '',
          firstLastName: user.firstLastName || '',
          nickname: user.nickname || '',
          birthDate: user.birthDate || '',
          nationality: user.nationality || '',
          naturality: user.naturality || '',
          professionalStatus: user.professionalStatus || 'Student',
          personalEmail: user.personalEmail || '',
          institutionalEmail: user.institutionalEmail || '',
          esnAccountUsername: user.esnAccountUsername || '',
          phone: user.phone || '',
          address: user.address || '',
          emergencyName: user.emergencyName || '',
          emergencyRelationship: user.emergencyRelationship || '',
          emergencyPhone: user.emergencyPhone || '',
          mobilityPlace: user.mobilityPlace || '',
          mobilityProgram: user.mobilityProgram || '',
          dateJoined: user.dateJoined || '',
          membershipType: user.membershipType || 'Newbie',
          departments: extractDepartmentIds(user.departments, catalog),
          position: user.position || 'Member',
          loginActive: user.loginActive === true,
          semesters: user.semesters || [],
          loginEmailType: user.institutionalEmail && user.email === user.institutionalEmail ? 'institutional' : 'personal',
          suspension: {
            isSuspended: user.suspension?.isSuspended || false,
            start: user.suspension?.start || '',
            end: user.suspension?.end || ''
          }
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form style={{ margin: '20px 50px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>

          <div className="edit-container">
            <div className="main-descriptions user-title" style={{ width: '100%', marginTop: '20px' }}>
              Profile Picture
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '10px' }}>
              {user.profilePictureUrl ? (
                <img
                  src={user.profilePictureUrl}
                  alt="Profile"
                  style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', marginBottom: '10px' }}
                />
              ) : (
                <div className="user-avatar">
                  <Icon icon="mdi:camera" className='user-edit-icon' />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
                <label className={`file-upload-btn ${uploadingPicture ? 'disabled' : ''}`}>
                  {uploadingPicture ? 'Uploading...' : 'CHOOSE PHOTO'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    disabled={uploadingPicture}
                    className="file-upload-input"
                  />
                </label>
                {user.profilePictureUrl && (
                  <button
                    type="button"
                    onClick={handleDeleteProfilePicture}
                    disabled={uploadingPicture}
                    className={`file-delete-btn`}
                  >
                    REMOVE PHOTO
                  </button>
                )}
              </div>
              {uploadingPicture}
              {pictureError && <p style={{ color: '#CD3E3D' }}>{pictureError}</p>}
            </div>
          </div>


            <div className="edit-form">
              {/* Personal Information Section */}
              <div className='edit-container'>
                <div className="main-descriptions user-title" style={{ width: '100%', marginTop: '20px' }}>
                  Personal Information
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="fullName">Full Name</label>
                  <Field
                    name="fullName"
                    className='edit-input'
                    onBlur={(e) => {
                      const trimmedValue = e.target.value.trim();
                      setFieldValue('fullName', trimmedValue);
                    }} />
                  <ErrorMessage name="fullName" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="firstLastName">First and Last Name (at your choice)</label>
                  <Field
                    name="firstLastName"
                    className='edit-input'
                    onBlur={(e) => {
                      const trimmedValue = e.target.value.trim();
                      setFieldValue('firstLastName', trimmedValue);
                    }} />
                  <ErrorMessage name="firstLastName" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="nickname">Nickname</label>
                  <Field
                    name="nickname"
                    className='edit-input'
                    onBlur={(e) => {
                      const trimmedValue = e.target.value.trim();
                      setFieldValue('nickname', trimmedValue);
                    }} />
                  <ErrorMessage name="nickname" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="birthDate">Date of Birth</label>
                  <Field name="birthDate" type="date" className='edit-input' />
                  <ErrorMessage name="birthDate" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="nationality">Nationality</label>
                  <Field name="nationality" className='edit-input' />
                  <ErrorMessage name="nationality" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="naturality">Naturality</label>
                  <Field name="naturality" className='edit-input' />
                  <ErrorMessage name="naturality" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignContent: 'center', marginTop: '5px' }}>
                  <label htmlFor="professionalStatus">Professional Status</label>
                  <div class="select-wrapper">
                    <Field as="select" name="professionalStatus" className="dropdown" style={{ width: '200px', paddingLeft: '20px' }}>
                      <option value="Student">Student</option>
                      <option value="Student-Worker">Student-Worker</option>
                      <option value="Employee">Employee</option>
                      <option value="Unemployed">Unemployed</option>
                      <option value="Other">Other</option>
                    </Field>
                  </div>
                </div>
              </div>
              

              {/* Contact Information Section */}
              <div className="edit-container">
                <div className="main-descriptions user-title" style={{ width: '100%', marginTop: '40px' }}>
                  Contact Information
                </div>

                {/* Info box for email update notice */}
                <div style={{
                  backgroundColor: 'rgba(0, 123, 255, 0.1)',
                  border: '1px solid rgba(0, 123, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '10px 15px',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <Icon icon="mdi:information-outline" width="1.2rem" height="1.2rem" style={{ color: '#007bff', flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '0.85rem', color: '#050505', margin: 0, lineHeight: '1.4' }}>
                    You can only update one email address at a time. <br/>
                    After submitting, you will receive a confirmation email to verify the change before it takes effect.
                  </p>
                </div>

                {/* Login Email Choice - Conditionally Rendered */}
                {user.institutionalEmail && (
                  <div style={{ marginBottom: '15px' }}>
                    <label>Which email address would you like to use for logging in?</label>
                    <div
                      className='radio-container-bordered' // Using a new conceptual class name for clarity
                      style={{
                        display: 'flex',
                        justifyContent: 'space-around', // This will space them out evenly inside the container
                        alignItems: 'center',
                        border: '1px solid black',
                        borderRadius: '1.5rem',
                        height: '3rem',
                        padding: '0 1rem',
                        marginTop: '5px'
                      }}
                    >
                      <div className="radio-select">
                        <Field type="radio" id="login-personal" name="loginEmailType" value="personal" />
                        <label htmlFor="login-personal">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Personal
                        </label>
                      </div>
                      <div className="radio-select">
                        <Field type="radio" id="login-institutional" name="loginEmailType" value="institutional" />
                        <label htmlFor="login-institutional">
                          <Icon icon="material-symbols:circle-outline" width="1.srem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          ESN
                        </label>
                      </div>
                    </div>
                  </div>
                )}


                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="personalEmail">Personal Email</label>
                  <Field name="personalEmail" type="email" className='edit-input' />
                  <ErrorMessage name="personalEmail" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="institutionalEmail">ESN Email</label>
                  <Field name="institutionalEmail" type="email" className='edit-input' />
                  <ErrorMessage name="institutionalEmail" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '5px' }}>
                  <label htmlFor="esnAccountUsername">ESN Account Username</label>
                  <Field
                    name="esnAccountUsername"
                    className='edit-input' />
                  <ErrorMessage name="esnAccountUsername" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="phone">Phone Number</label>
                  <Field name="phone" className='edit-input' />
                  <ErrorMessage name="phone" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '15px' }}>
                  <label htmlFor="address">Address</label>
                  <Field name="address" className='edit-input' />
                  <ErrorMessage name="address" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                {/* Emergency Contact Section */}
                <div className="main-descriptions user-title" style={{ width: '100%', marginTop: '40px' }}>
                  Emergency Contact
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="emergencyName">Emergency Contact Name</label>
                  <Field name="emergencyName" className='edit-input' />
                  <ErrorMessage name="emergencyName" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="emergencyRelationship">Relationship</label>
                  <Field name="emergencyRelationship" className='edit-input' />
                  <ErrorMessage name="emergencyRelationship" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label htmlFor="emergencyPhone">Emergency Contact Phone</label>
                  <Field name="emergencyPhone" className='edit-input' />
                  <ErrorMessage name="emergencyPhone" component="div" style={{ color: '#CD3E3D' }} />
                </div>
              </div>

              {/* ESN Experience Section */}
              <div className="edit-container">
                <div className="main-descriptions user-title" style={{ width: '100%', marginTop: '40px' }}>
                  ESN Experience
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="mobilityPlace">Mobility Place</label>
                  <Field name="mobilityPlace" className='edit-input' />
                  <ErrorMessage name="mobilityPlace" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="mobilityProgram">Mobility Program</label>
                  <Field name="mobilityProgram" className='edit-input' />
                  <ErrorMessage name="mobilityProgram" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                  <label htmlFor="dateJoined">Date Joined ESN</label>
                  <Field name="dateJoined" type="date" className='edit-input' />
                  <ErrorMessage name="dateJoined" component="div" style={{ color: '#CD3E3D' }} />
                </div>

                {/* Semesters Section - Only for admins */}
                {canEditAdminFields && (
                  <div style={{ marginBottom: '5px' }}>
                    <label>Active Semesters</label>
                    <div style={{}}>
                      {semesters.map((semester) => (
                        <div key={semester._id} className="radio-select">
                          <Field
                            type="checkbox"
                            id={`semester-${semester._id}`}
                            name="semesters"
                            value={semester._id}
                          />
                          <label htmlFor={`semester-${semester._id}`}>
                            <Icon icon="mdi:checkbox-blank-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                            <Icon icon="mdi:checkbox-marked-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                            {semester.name}
                            {semester.isActive && <> <span style={{ color: '#28a745', fontSize: '0.8em' }}>&nbsp; (Current)</span></>}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage name="semesters" component="div" style={{ color: '#CD3E3D' }} />
                  </div>
                )}

                {/* Membership Type - Conditionally Rendered */}
                {canEditAdminFields ? (
                  <div style={{ marginBottom: '5px' }}>
                    <label>Membership Type</label>
                    <div className='radio-container membership'>
                      <div className="radio-select">
                        <Field type="radio" id="edit-newbie" name="membershipType" value="Newbie" />
                        <label htmlFor="edit-newbie">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Newbie
                        </label>
                      </div>

                      <div className="radio-select">
                        <Field type="radio" id="edit-junior" name="membershipType" value="Junior" />
                        <label htmlFor="edit-junior">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Junior
                        </label>
                      </div>

                      <div className="radio-select">
                        <Field type="radio" id="edit-senior" name="membershipType" value="Senior" />
                        <label htmlFor="edit-senior">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Senior
                        </label>
                      </div>

                      <div className="radio-select">
                        <Field type="radio" id="edit-alumni" name="membershipType" value="Alumni" />
                        <label htmlFor="edit-alumni">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Alumni
                        </label>
                      </div>

                      <div className="radio-select">
                        <Field type="radio" id="edit-intern" name="membershipType" value="Intern" />
                        <label htmlFor="edit-intern">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Intern
                        </label>
                      </div>

                      <div className="radio-select">
                        <Field type="radio" id="edit-unbound" name="membershipType" value="Unbound" />
                        <label htmlFor="edit-unbound">
                          <Icon icon="material-symbols:circle-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                          <Icon icon="material-symbols:circle" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                          Unbound
                        </label>
                      </div>
                    </div>
                    <ErrorMessage name="membershipType" component="div" style={{ color: '#CD3E3D' }} />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                    <label htmlFor="membershipType">Membership Type</label>
                    <Field
                      name="membershipType"
                      type="text"
                      className='edit-input'
                      readOnly
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                  </div>
                )}

                {/* Departments - Conditionally Rendered */}
                {canEditAdminFields ? (
                  <div style={{ marginBottom: '5px' }}>
                    <label>Departments</label>
                    <DepartmentCheckboxGroup />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                    <label htmlFor="departments">Departments</label>
                    <Field
                      as="textarea"
                      name="departments"
                      className='edit-input'
                      readOnly
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed', height: 'auto', resize: 'none' }}
                      value={values.departments
                        .map((id) => {
                          const match = catalog.find((d) => String(d._id) === String(id));
                          return match?.name || id;
                        })
                        .join(', ')}
                    />
                  </div>
                )}

                {/* Position - Conditionally Rendered */}
                {canEditAdminFields ? (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginBottom: '5px' }}>
                    <label htmlFor="position">Position</label>
                    <div class="select-wrapper">
                      <Field as="select" name="position" className="dropdown" style={{ width: '100%', paddingLeft: '20px' }}>
                        <option value="Member">Member</option>
                        {getHeadOfPositions(catalog).map((position) => (
                          <option key={position} value={position}>
                            {formatPositionOptionLabel(position)}
                          </option>
                        ))}
                        {ADDITIONAL_POSITIONS.map((position) => (
                          <option key={position} value={position}>
                            {position === "Activities Officer" ? "Activites Officer" : position}
                          </option>
                        ))}
                      </Field>
                    </div>
                    <ErrorMessage name="position" component="div" style={{ color: '#CD3E3D' }} />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>
                    <label htmlFor="position">Position</label>
                    <Field
                      name="position"
                      type="text"
                      className='edit-input'
                      readOnly
                      style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                  </div>
                )}

                {canEditUserLoginActive(contextUser) && !isFormerMember(user) && (
                  <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Field type="checkbox" name="loginActive" id="loginActive" />
                    <label htmlFor="loginActive">Platform login active (HR approved)</label>
                  </div>
                )}
                {canEditUserLoginActive(contextUser) && isFormerMember(user) && (
                  <p style={{ marginBottom: '5px', color: '#6c757d' }}>
                    Alumni and Unbound members cannot log in. Change membership type to restore access.
                  </p>
                )}

              </div>

              {/* Suspension Section - Only for admins */}
              {canEditAdminFields && (
                <div className="edit-container">
                  <div className="main-descriptions user-title" style={{ width: '100%', marginTop: '40px' }}>
                    Suspension Status
                  </div>

                  <div className="radio-select" style={{ marginBottom: '15px' }}>
                    <Field
                      type="checkbox"
                      id="suspension-checkbox"
                      name="suspension.isSuspended"
                      onChange={(e) => {
                        setFieldValue('suspension.isSuspended', e.target.checked);
                        if (!e.target.checked) {
                          setFieldValue('suspension.start', '');
                          setFieldValue('suspension.end', '');
                        }
                      }}
                    />
                    <label htmlFor="suspension-checkbox">
                      <Icon icon="mdi:checkbox-blank-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-empty' />
                      <Icon icon="mdi:checkbox-marked-outline" width="1.5rem" height="1.5rem" className='radio-icon radio-full' />
                      Member is suspended
                    </label>
                  </div>

                  {values.suspension?.isSuspended && (
                    <div>
                      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '15px' }}>
                        <label htmlFor="suspension.start">Suspension Start Date</label>
                        <Field name="suspension.start" type="date" className='edit-input' />
                        <ErrorMessage name="suspension.start" component="div" style={{ color: '#CD3E3D' }} />
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '15px' }}>
                        <label htmlFor="suspension.end">Suspension End Date</label>
                        <Field name="suspension.end" type="date" className='edit-input' />
                        <ErrorMessage name="suspension.end" component="div" style={{ color: '#CD3E3D' }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>


            {/* Submit Button */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', marginBottom: '40px' }}>
              <button
                type="submit"
                className='button'
                disabled={isSubmitting}
                style={{ maxWidth: '200px', minWidth: '160px' }}
              >
                {isSubmitting ? 'Updating...' : 'Update Profile'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
      {/* Alert that shows upon event submission */}
      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}

      {alertActionInfo.show && (
        <CustomAlertWithButtons
          title={alertActionInfo.title}
          type={alertActionInfo.type}
          message={alertActionInfo.message}
          onButton1Click={alertActionInfo.btn1Action}
          onButton2Click={alertActionInfo.btn2Action}
          button1Text={alertActionInfo.btn1Text}
          button2Text={alertActionInfo.btn2Text}
        />)}
    </div>
  );
};

export default EditProfile;
